import {TechEdgePoint} from "../../models/remoteConstants.model";
import {cellToWorld} from "../../utils/graphCanvas";

// Edge routing for the tech tree.
//
// An edge with NO authored bends is a straight line, parent centre to child
// centre. Bending an edge is what opts it into orthogonal routing: it then runs
// parent -> each bend in order -> child, with every leg axis-aligned ('h' turns
// horizontally first then vertically, 'v' the other way round). A leg whose
// endpoints already share an x or a y is a plain straight segment and the flag
// makes no difference — the common case, since bends get dragged along the line
// they came from.
//
// Keeping unbent edges straight matters because the default layout is radial:
// forcing every diagonal spoke into a staircase would turn a tidy tree into a
// maze before anyone had authored anything.
//
// Edges are painted before nodes so the centre-to-centre ends disappear under
// the opaque node circles; there's no need to trim the path to the circle edge.

export interface WorldPoint {
    x: number;
    y: number;
}

export type Axis = 'h' | 'v';

// The full vertex list, including the generated corners. Used both to build the
// SVG path and to hit-test legs when inserting a new bend.
function orthPolyline(
    from: WorldPoint,
    points: TechEdgePoint[] | undefined,
    to: WorldPoint,
    endAxis?: Axis,
): WorldPoint[] {
    // Unbent: a direct line, no generated corner.
    if (!points || points.length === 0) return [from, to];

    const legs: {target: WorldPoint; axis: Axis}[] = [];
    for (const point of points ?? []) {
        const [x, y] = cellToWorld(point.x, point.y);
        legs.push({target: {x, y}, axis: point.axis ?? 'h'});
    }
    legs.push({target: to, axis: endAxis ?? 'h'});

    const vertices: WorldPoint[] = [from];
    let current = from;
    for (const leg of legs) {
        const corner = leg.axis === 'h'
            ? {x: leg.target.x, y: current.y}
            : {x: current.x, y: leg.target.y};
        if (corner.x !== current.x || corner.y !== current.y) vertices.push(corner);
        if (leg.target.x !== corner.x || leg.target.y !== corner.y) vertices.push(leg.target);
        current = leg.target;
    }

    return vertices;
}

// Corner fillet radius, in world px — so it scales with the graph like the
// stroke width does, rather than growing relative to everything else as you
// zoom out.
const CORNER_R = 8;

// Arrowhead at the child end of an edge, world px. Deliberately small — sized
// against the 3px stroke so it reads as a direction tick rather than a glyph.
const ARROW_LEN = 7;
const ARROW_HALF_W = 4;

// The drawn line stops at the middle of the arrowhead rather than running on to
// the child's edge: the stroke is narrower than the arrow's base, so carrying it
// through to the tip leaves it poking out of the triangle. Ending half an
// arrow-length short tucks it under the head, and the head's own fill covers the
// join.
export function orthPath(
    from: WorldPoint,
    points: TechEdgePoint[] | undefined,
    to: WorldPoint,
    endAxis: Axis | undefined,
    nodeRadius: number,
): string {
    const vertices = orthPolyline(from, points, to, endAxis);
    return roundedPath(trimEnd(vertices, nodeRadius + ARROW_LEN / 2), CORNER_R);
}

// Pulls the final vertex back along the last leg. Clamped to the leg's own
// length, so nodes close enough to overlap collapse that leg to nothing instead
// of the line doubling back on itself.
function trimEnd(vertices: WorldPoint[], trim: number): WorldPoint[] {
    if (vertices.length < 2 || trim <= 0) return vertices;
    const end = vertices[vertices.length - 1];
    const prev = vertices[vertices.length - 2];
    const length = distance(prev, end);
    if (length < 1e-6) return vertices;

    const trimmed = vertices.slice();
    trimmed[trimmed.length - 1] = towards(end, prev, Math.min(trim, length));
    return trimmed;
}

// The polyline as an SVG path with each corner replaced by a quadratic fillet.
// The radius is clamped to half of the shorter adjacent leg so a tight bend
// can't overshoot into the previous or next segment.
function roundedPath(vertices: WorldPoint[], radius: number): string {
    if (vertices.length === 0) return '';

    let d = `M ${fmt(vertices[0])}`;
    for (let i = 1; i < vertices.length - 1; i++) {
        const prev = vertices[i - 1];
        const curr = vertices[i];
        const next = vertices[i + 1];
        const r = Math.min(radius, distance(prev, curr) / 2, distance(curr, next) / 2);
        if (r <= 0) {
            // Degenerate (a repeated point) — keep the vertex so the path can't
            // short-cut across it.
            d += ` L ${fmt(curr)}`;
            continue;
        }
        // Pull back off the corner, curve through it, and leave along the next
        // leg. Collinear vertices degenerate to a straight line, harmlessly.
        d += ` L ${fmt(towards(curr, prev, r))} Q ${fmt(curr)} ${fmt(towards(curr, next, r))}`;
    }

    return `${d} L ${fmt(vertices[vertices.length - 1])}`;
}

// SVG `points` for the arrowhead where an edge meets its child. The tip sits on
// the child circle's edge rather than at its centre, because edges are drawn
// centre-to-centre and vanish under the opaque node. Returns undefined when the
// final leg has no length to take a direction from.
export function orthArrow(
    from: WorldPoint,
    points: TechEdgePoint[] | undefined,
    to: WorldPoint,
    endAxis: Axis | undefined,
    nodeRadius: number,
): string | undefined {
    const vertices = orthPolyline(from, points, to, endAxis);
    if (vertices.length < 2) return undefined;

    const end = vertices[vertices.length - 1];
    const prev = vertices[vertices.length - 2];
    const length = distance(prev, end);
    if (length < 1e-6) return undefined;

    const ux = (end.x - prev.x) / length;
    const uy = (end.y - prev.y) / length;
    const tipX = end.x - ux * nodeRadius;
    const tipY = end.y - uy * nodeRadius;
    const baseX = tipX - ux * ARROW_LEN;
    const baseY = tipY - uy * ARROW_LEN;
    // Perpendicular to the direction of travel.
    const px = -uy;
    const py = ux;

    return [
        `${round(tipX)},${round(tipY)}`,
        `${round(baseX + px * ARROW_HALF_W)},${round(baseY + py * ARROW_HALF_W)}`,
        `${round(baseX - px * ARROW_HALF_W)},${round(baseY - py * ARROW_HALF_W)}`,
    ].join(' ');
}

function distance(a: WorldPoint, b: WorldPoint): number {
    return Math.hypot(b.x - a.x, b.y - a.y);
}

// The point `dist` away from `from`, heading towards `target`.
function towards(from: WorldPoint, target: WorldPoint, dist: number): WorldPoint {
    const length = distance(from, target);
    if (length < 1e-6) return from;
    return {
        x: from.x + ((target.x - from.x) / length) * dist,
        y: from.y + ((target.y - from.y) / length) * dist,
    };
}

// Two decimals is well under a pixel at any usable zoom and keeps the emitted
// path strings short.
function round(n: number): number {
    return Math.round(n * 100) / 100;
}

function fmt(p: WorldPoint): string {
    return `${round(p.x)} ${round(p.y)}`;
}

// Squared distance from p to segment ab, and the closest point on it. Squared
// so callers can compare against a threshold without a sqrt per segment.
function closestOnSegment(p: WorldPoint, a: WorldPoint, b: WorldPoint): {point: WorldPoint; distSq: number} {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
    const point = {x: a.x + t * dx, y: a.y + t * dy};
    return {point, distSq: (p.x - point.x) ** 2 + (p.y - point.y) ** 2};
}

// Where along an edge a new bend should be inserted for a click at `p`.
// Returns the index to splice into `points`, or undefined when the click isn't
// within `tolerance` world px of the edge.
//
// The vertex list contains generated corners as well as authored bends, so the
// insert index is derived by counting how many authored bends the closest
// segment falls after — a corner belongs to the leg that produced it.
export function insertIndexAt(
    p: WorldPoint,
    from: WorldPoint,
    points: TechEdgePoint[] | undefined,
    to: WorldPoint,
    endAxis: Axis | undefined,
    tolerance: number,
): {index: number; point: WorldPoint} | undefined {
    const authored = points ?? [];
    const vertices = orthPolyline(from, authored, to, endAxis);

    let best: {index: number; point: WorldPoint; distSq: number} | undefined;
    // Walk the vertices, tracking how many authored bends we've passed. An
    // authored bend is a vertex that coincides with one of `authored`.
    let passed = 0;
    for (let i = 0; i < vertices.length - 1; i++) {
        const hit = closestOnSegment(p, vertices[i], vertices[i + 1]);
        if (!best || hit.distSq < best.distSq) best = {index: passed, point: hit.point, distSq: hit.distSq};
        if (isAuthored(vertices[i + 1], authored)) passed++;
    }

    if (!best || best.distSq > tolerance * tolerance) return undefined;
    return {index: best.index, point: best.point};
}

function isAuthored(v: WorldPoint, authored: TechEdgePoint[]): boolean {
    return authored.some(point => {
        const [x, y] = cellToWorld(point.x, point.y);
        return x === v.x && y === v.y;
    });
}
